import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Badge from 'core/elements/badge/Badge'
import { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import { middleLeft } from 'core/elements/menu/defaults'
import Tooltip from 'core/elements/tooltip'
import Theme from 'core/themes/model'
import moment from 'moment'
import {
  completedStatuses,
  failedStatuses,
  pendingStatuses,
} from 'openstack/components/infrastructure/hosts/host-details/MaintenanceModeStatusBar'
import { IVirtualMachinesSelector } from './newModel'

const withinLastFiveMinutes = (timestamp) => {
  const comparisonTime = moment()
    .utc()
    .subtract(5, 'minutes')
    .format()
  return timestamp > comparisonTime
}

const getBadgeVariant = (status) =>
  ({
    active: 'success',
    paused: 'warning',
    stopped: 'error',
    error: 'error',
    suspended: 'warning',
    building: 'success',
    rescued: 'warning',
    migrating: 'warning',
  }[status] || 'unknown')

const getBadgeText = (status, pausedPowerState, taskState) => {
  const text =
    {
      active: 'Active',
      paused: 'Paused',
      stopped: 'Stopped',
      error: 'Error',
      suspended: 'Suspended',
      building: 'Building',
      rescued: 'Rescued',
      migrating: 'Migrating',
    }[status] || status
  return taskState ? `${text} (${taskState})` : pausedPowerState ? `${text} (paused)` : text
}

interface VmStateCellComponentProps extends GridCellProps<IVirtualMachinesSelector> {
  state: string
  setErrorVm?: (value: string) => void
  setMigrationFailureVm?: (value: any) => void
  hosts?: any[]
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    cursor: 'default',
  },
  rootError: {
    cursor: 'pointer',
  },
  icon: {
    marginRight: 5,
  },
  error: {
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
  migration: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    marginTop: 8,
  },
  circleIcon: {
    fontSize: '18px',
    color: '#fff',
  },
  circleSuccess: {
    background: theme.palette.graphGreen[100],
    color: theme.palette.graphGreen[500],
  },
  circleMigrationError: {
    background: theme.palette.graphRed[100],
    color: theme.palette.graphRed[500],
  },
  circleMigrationInprogress: {
    background: theme.palette.graphYellow[100],
    color: theme.palette.graphYellow[500],
  },
  italic: {
    color: theme.components.typography.passive,
  },
}))

const hostOffline = (host) => {
  if (host?.info?.responding === false) {
    return true
  }
  return false
}

export default function VmStateCellComponent({
  item: {
    id,
    state,
    taskState,
    pausedPowerState,
    migration,
    migrationAttemptedFrom,
    migrationDestination,
    ...rest
  },
  setErrorVm,
  setMigrationFailureVm,
  hosts,
}: VmStateCellComponentProps) {
  const classes = useStyles()
  const isErrorState = state && state.toLowerCase() === 'error'
  const currentHost = rest?.['OS-EXT-SRV-ATTR:hypervisor_hostname']

  const hostIsOffline = hosts?.length
    ? hostOffline(hosts.find((host) => host.id === rest?.['OS-EXT-SRV-ATTR:host']))
    : false

  return (
    <>
      <div className={clsx(classes.root, isErrorState && classes.rootError)}>
        {/* {taskState && (
      <FontAwesomeIcon className={classes.icon} spin>
        sync
      </FontAwesomeIcon>
    )} */}
        {!taskState && state === 'resized' && (
          <Tooltip message="Resize confirmation action required">
            <FontAwesomeIcon className={classes.icon}>circle-exclamation</FontAwesomeIcon>
          </Tooltip>
        )}
        {isErrorState ? (
          <div
            className={classes.error}
            onClick={(e) => {
              e.stopPropagation()
              if (isErrorState && setErrorVm) setErrorVm(id)
            }}
          >
            <Badge
              variant={getBadgeVariant(state)}
              text={getBadgeText(state, pausedPowerState, taskState)}
            />
          </div>
        ) : pendingStatuses.includes(migration?.status) ? (
          <Tooltip
            align={middleLeft.align}
            offset={middleLeft.offset}
            message={
              <div>
                <div>Migration in-progress</div>
                <div>from: {migration?.source_node || migration?.source_compute || 'N/A'}</div>
                <div>to: {migration?.dest_node || migration?.dest_compute || 'N/A'}</div>
              </div>
            }
          >
            <Badge variant="warning" text="Migrating" />
          </Tooltip>
        ) : (
          <Badge
            variant={getBadgeVariant(state)}
            text={getBadgeText(state, pausedPowerState, taskState)}
          />
        )}
      </div>
      {/* Do not show migration success/fail when migrating bc they may have triggered
      another migration within past few minutes that finished & selector may pick up
      the previous migration */}
      {state !== 'migrating' &&
      withinLastFiveMinutes(migration?.updatedTimestamp || migration?.createdTimestamp) ? (
        completedStatuses.includes(migration?.status) ? (
          <div className={classes.migration}>
            <Tooltip
              align={middleLeft.align}
              offset={middleLeft.offset}
              message={
                <div>
                  <div>Migration successful</div>
                  <div>from: {migration?.source_node || migration?.source_compute || 'N/A'}</div>
                  <div>to: {migration?.dest_node || migration?.dest_compute || 'N/A'}</div>
                </div>
              }
            >
              <FontAwesomeIcon solid className={clsx(classes.circleIcon, classes.circleSuccess)}>
                circle-check
              </FontAwesomeIcon>
            </Tooltip>
            migration succeeded
          </div>
        ) : failedStatuses.includes(migration?.status) ? (
          <div
            className={classes.migration}
            onClick={(e) => {
              e.stopPropagation()
              if (setMigrationFailureVm) {
                setMigrationFailureVm({ id, ...rest })
              }
            }}
          >
            <Tooltip
              align={middleLeft.align}
              offset={middleLeft.offset}
              message={
                <div>
                  <div>Migration failed</div>
                  <div>from: {migration?.source_node || migration?.source_compute || 'N/A'}</div>
                  <div>to: {migration?.dest_node || migration?.dest_compute || 'N/A'}</div>
                </div>
              }
            >
              <FontAwesomeIcon
                solid
                className={clsx(classes.circleIcon, classes.circleMigrationError)}
              >
                circle-xmark
              </FontAwesomeIcon>
            </Tooltip>
            <span className={classes.error}>migration failed</span>
          </div>
        ) : null
      ) : null}
      {hostIsOffline ? (
        <Tooltip
          align={middleLeft.align}
          offset={middleLeft.offset}
          message={
            <div>
              <div>Last known state: {getBadgeText(state, pausedPowerState, taskState)}</div>
              <div>Current state: Unknown</div>
            </div>
          }
        >
          <div className={classes.italic}>
            (<i data-testid="vm-state-cell-component--i-0">host offline</i>)
          </div>
        </Tooltip>
      ) : null}
    </>
  )
}
